import { RedoShopifyDeliveryMethodType } from "@redotech/redo-fulfillment-group-common/fulfillment-delivery-method-type";
import {
  FulfillmentGroup,
  FulfillmentGroupData,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { FulfillmentGroupStatus } from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { ShipmentTrackerStatus } from "@redotech/redo-fulfillment-group-common/outbound-shipment-label";
import { RateProvider } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import {
  canVoidDhlByCountriesAndAge,
  canVoidProviderLabel,
} from "@redotech/redo-fulfillment-rate-common/void-eligibility";

export function canVoidFreight(
  row: FulfillmentGroupData | FulfillmentGroup,
): boolean {
  const hasFreightShipment = !!row.shipmentDetails?.freightShipment;
  const alreadyVoided = row.shipmentDetails?.voided;
  const isClosed =
    row.status === FulfillmentGroupStatus.Closed ||
    row.status === FulfillmentGroupStatus.Cancelled;

  return !!hasFreightShipment && !alreadyVoided && isClosed;
}

export function canVoidLabel(row: FulfillmentGroupData | FulfillmentGroup) {
  const hasBoughtLabel = !!row.shipment?.boughtRate;
  const alreadyVoided = row.shipment?.voided;
  const isClosed =
    row.status === FulfillmentGroupStatus.Closed ||
    row.status === FulfillmentGroupStatus.Cancelled;
  const trackingCode = row.shipment?.tracker?.trackingCode;

  const hasShippedAccordingToTracker =
    !!trackingCode &&
    !!row.shipment?.tracker?.status &&
    [
      ShipmentTrackerStatus.IN_TRANSIT,
      ShipmentTrackerStatus.STALLED_IN_TRANSIT,
      ShipmentTrackerStatus.OUT_FOR_DELIVERY,
      ShipmentTrackerStatus.DELIVERED,
    ].includes(row.shipment?.tracker?.status);

  const canVoidDhlLabel = canVoidDhl(row);
  const canVoidTiktokLabel = canVoidTiktok(row);

  return (
    !alreadyVoided &&
    isClosed &&
    !hasShippedAccordingToTracker &&
    hasBoughtLabel &&
    canVoidDhlLabel &&
    canVoidTiktokLabel
  );
}

const canVoidTiktok = (row: FulfillmentGroupData | FulfillmentGroup) => {
  const boughtRate = row.shipment?.boughtRate;
  return !boughtRate?.provider || canVoidProviderLabel(boughtRate.provider);
};

const canVoidDhl = (row: FulfillmentGroupData | FulfillmentGroup) => {
  const boughtRate = row.shipment?.boughtRate;
  if (!boughtRate || boughtRate.provider !== RateProvider.DHL_ECOMMERCE) {
    return true;
  }

  let originCountry: string | null | undefined;
  let destinationCountry: string | null | undefined;

  // Check if row has direct address fields (FulfillmentGroupData)
  if ("originAddress" in row && "destinationAddress" in row) {
    originCountry = row.originAddress?.countryCode;
    destinationCountry = row.destinationAddress?.countryCode;
  } else if ("summary" in row) {
    originCountry = row.summary.originAddress?.countryCode;
    destinationCountry = row.summary.destinationAddress?.countryCode;
  }

  // If we've already manifested the label, we can't void it
  if (row.shipment?.scanFormId) {
    return false;
  }

  return canVoidDhlByCountriesAndAge({
    originCountry,
    destinationCountry,
    createdAt: row.shipment?.createdAt,
  });
};

export function getGroupDeliveryMethod(
  group: FulfillmentGroup,
): RedoShopifyDeliveryMethodType {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return (
    group.overrideDeliveryMethod ??
    (group.fulfillmentOrders.some(
      ({ deliveryMethod }) =>
        deliveryMethod.kind === RedoShopifyDeliveryMethodType.Shipping,
    )
      ? RedoShopifyDeliveryMethodType.Shipping
      : group.fulfillmentOrders[0]?.deliveryMethod?.kind)
  );
}
